import React, { useEffect, useState } from 'react';
import { Grid2X2, Heart, List, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import LoadingIndicator from '../components/LoadingIndicator';
import UserSectionSidebar from '../components/UserSectionSidebar';
import { Product } from '../services/api.types';
import { fetchFavoriteProducts } from '../services/product.api';
import { useTranslations } from '../translations';
import './Favorites.css';

interface FavoritesProps {
  isLoggedIn: boolean;
  isAuthChecking: boolean;
  isAdmin?: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToAdministration?: () => void;
  onLogout: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToProductDetail: (productId: string) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToContact?: () => void;
}

const PAGE_SIZE = 10;
type FavoriteProductType = 'PRODUCT' | 'DEMAND';

const getInitialProductType = (): FavoriteProductType => {
  const productType = new URLSearchParams(window.location.search).get('productType');
  return productType === 'DEMAND' ? 'DEMAND' : 'PRODUCT';
};

const Favorites: React.FC<FavoritesProps> = ({
  isLoggedIn,
  isAuthChecking,
  isAdmin = false,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToAdministration,
  onLogout,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToProductDetail,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact
}) => {
  const t = useTranslations();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [viewType, setViewType] = useState<'list' | 'grid'>('list');
  const [productTypeFilter, setProductTypeFilter] = useState<FavoriteProductType>(getInitialProductType);
  const [refreshRevision, setRefreshRevision] = useState(0);

  useEffect(() => {
    if (isAuthChecking || !isLoggedIn || isAdmin) return;

    let active = true;
    const loadFavorites = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await fetchFavoriteProducts(currentPage, PAGE_SIZE, productTypeFilter);
        if (!active) return;
        setProducts(response.content);
        setTotalElements(response.totalElements);
        setTotalPages(Math.max(response.totalPages, 1));
      } catch (requestError) {
        if (!active) return;
        console.error('Error loading favorite products:', requestError);
        setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadFavorites();
    return () => {
      active = false;
    };
  }, [currentPage, isAdmin, isAuthChecking, isLoggedIn, productTypeFilter, refreshRevision]);

  const handleProductTypeChange = (nextProductType: FavoriteProductType) => {
    if (nextProductType === productTypeFilter) return;

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('productType', nextProductType);
    window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}`);
    setCurrentPage(1);
    setProductTypeFilter(nextProductType);
  };

  const handleFavoriteChange = (productId: string, favorite: boolean) => {
    if (favorite) return;

    if (products.length === 1 && currentPage > 1) {
      setCurrentPage((page) => page - 1);
      return;
    }

    setProducts((current) => current.filter((product) => product.id !== productId));
    const nextTotal = Math.max(0, totalElements - 1);
    setTotalElements(nextTotal);
    setTotalPages(Math.max(1, Math.ceil(nextTotal / PAGE_SIZE)));
    setRefreshRevision((revision) => revision + 1);
  };

  return (
    <div className="favorites-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onAdministrationClick={onNavigateToAdministration}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <div className="favorites-container">
        <UserSectionSidebar
          activeItem="favorites"
          onProfileClick={onNavigateToProfile}
          onLocationsClick={onNavigateToLocations}
          onMessagesClick={() => onNavigateToMessages?.()}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onSettingsClick={onNavigateToSettings}
        />

        <main className="favorites-main">
          <section className="favorites-hero">
            <div className="favorites-heading-icon" aria-hidden="true">
              <Heart size={22} />
            </div>
            <div>
              <h1 className="favorites-title">{t.favorites.title}</h1>
              <p className="favorites-subtitle">{t.favorites.subtitle}</p>
            </div>
            <span className="favorites-count">
              {totalElements === 1
                ? t.favorites.savedOne
                : t.favorites.savedCount.replace('{count}', totalElements.toString())}
            </span>
          </section>

          <div className="favorites-toolbar">
            <div className="favorites-filter-section">
              <span className="favorites-filter-label">{t.favorites.filterLabel}</span>
              <div className="favorites-type-filter" role="group" aria-label={t.favorites.filterLabel}>
                {([
                  ['PRODUCT', t.favorites.filterSell],
                  ['DEMAND', t.favorites.filterBuy]
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={productTypeFilter === value ? 'active' : ''}
                    onClick={() => handleProductTypeChange(value)}
                    aria-pressed={productTypeFilter === value}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="favorites-view-switch" role="group" aria-label={t.favorites.viewType}>
              <button
                type="button"
                className={viewType === 'list' ? 'active' : ''}
                onClick={() => setViewType('list')}
                aria-label={t.favorites.listView}
                aria-pressed={viewType === 'list'}
              >
                <List size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={viewType === 'grid' ? 'active' : ''}
                onClick={() => setViewType('grid')}
                aria-label={t.favorites.gridView}
                aria-pressed={viewType === 'grid'}
              >
                <Grid2X2 size={17} aria-hidden="true" />
              </button>
            </div>
          </div>

          {loading ? (
            <LoadingIndicator message={t.favorites.loading} className="favorites-state" />
          ) : error ? (
            <div className="favorites-state favorites-error">{t.favorites.error}</div>
          ) : products.length === 0 ? (
            <div className="favorites-empty">
              <HeartEmpty />
              <h2>{t.favorites.emptyFilteredTitle}</h2>
              <p>{t.favorites.emptyFilteredDescription}</p>
            </div>
          ) : (
            <div className={viewType === 'list' ? 'favorites-list' : 'favorites-grid'}>
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  viewType={viewType}
                  onNavigateToProductDetail={onNavigateToProductDetail}
                  isLoggedIn={isLoggedIn}
                  onNavigateToLogin={onNavigateToLogin}
                  onFavoriteChange={handleFavoriteChange}
                />
              ))}
            </div>
          )}

          {!loading && !error && totalPages > 1 && (
            <div className="favorites-pagination">
              <button
                type="button"
                onClick={() => setCurrentPage((page) => page - 1)}
                disabled={currentPage === 1}
                aria-label={t.favorites.previousPage}
              >
                <ChevronLeft size={20} aria-hidden="true" />
              </button>
              <span>{currentPage} / {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage((page) => page + 1)}
                disabled={currentPage === totalPages}
                aria-label={t.favorites.nextPage}
              >
                <ChevronRight size={20} aria-hidden="true" />
              </button>
            </div>
          )}
        </main>
      </div>

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToContact={onNavigateToContact}
      />
    </div>
  );
};

const HeartEmpty = () => (
  <div className="favorites-empty-icon" aria-hidden="true">
    <Heart size={24} />
  </div>
);

export default Favorites;
